/**
 *
 * CheckInInvitations
 *
 * Per-person history of six-monthly check-in invitations. Lives on
 * the person edit page as a tab. See API ADR-0002.
 *
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { apiRequest } from 'utils/request';
import { LoadingIndicator } from '../../components/LoadingIndicator';

interface Row {
  id: number;
  key: string;
  status: number;
  last_sent_at: string | null;
  responded_at: string | null;
  batch_id: number | null;
}

interface Props {
  personId: number;
}

const statusLabel = (row: Row) => {
  if (row.responded_at)
    return { label: 'Responded', color: 'success' as const };
  if (row.status === 11)
    return { label: 'Cancelled', color: 'default' as const };
  if (row.status === 3) return { label: 'Nudged', color: 'warning' as const };
  return { label: 'Awaiting response', color: 'info' as const };
};

export function CheckInInvitations({ personId }: Props) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest({
          url: `people/${personId}/check-in-invitations`,
          method: 'get',
        });
        if (!cancelled) setRows(res.data);
      } catch (e: any) {
        if (!cancelled) setError(e?.payload?.message || 'Failed to load.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId]);

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (rows === null) return <LoadingIndicator />;

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No check-in emails have been sent to this person yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">Check-in history</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sent</TableCell>
              <TableCell>Responded</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const status = statusLabel(row);
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.last_sent_at
                      ? new Date(row.last_sent_at).toLocaleString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {row.responded_at
                      ? new Date(row.responded_at).toLocaleString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={status.color}
                      label={status.label}
                    />
                  </TableCell>
                  <TableCell>
                    {row.batch_id ? `Batch #${row.batch_id}` : 'Single send'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
    </Box>
  );
}
